import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Section, Empty } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import api, { errMsg } from "@/lib/api";

const today = () => new Date().toLocaleDateString("en-CA");

function fromDay(day) {
  return (day?.exercises || []).map((e) => ({
    exercise: e.name,
    notes: "",
    target_rest: e.rest || "",
    target_weight: e.weight !== "" && e.weight != null ? `${e.weight} ${e.weight_unit || "kg"}` : "",
    target_time: e.time || "",
    target_distance: e.distance || "",
    time: "",
    distance: "",
    sets: Array.from({ length: Number(e.sets) || (e.time || e.distance ? 1 : 3) }, () => ({
      reps: parseInt(e.reps, 10) || 0,
      weight: Number(e.weight) || 0,
      weight_unit: e.weight_unit || "kg",
    })),
  }));
}

function Stepper({ value, onChange, step, testid }) {
  return (
    <div className="flex items-center">
      <button type="button" className="px-1.5 text-slate-400 hover:text-white" onClick={() => onChange(Math.max(0, (value || 0) - step))}><Minus size={12} /></button>
      <Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className="h-8 w-16 text-center bg-[#111827] px-1" data-testid={testid} />
      <button type="button" className="px-1.5 text-slate-400 hover:text-white" onClick={() => onChange((value || 0) + step)}><Plus size={12} /></button>
    </div>
  );
}

function LogDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  if (!f) return null;
  const upd = (ei, patch) => setF({ ...f, entries: f.entries.map((e, j) => (j === ei ? { ...e, ...patch } : e)) });
  const updSet = (ei, si, k, v) => upd(ei, { sets: f.entries[ei].sets.map((s, j) => (j === si ? { ...s, [k]: v } : s)) });
  const save = async () => {
    try {
      const body = { date: f.date, plan_id: f.plan_id, day_name: f.day_name, entries: f.entries, duration_min: f.duration_min || null, rpe: f.rpe || null, notes: f.notes };
      if (f.id) await api.put(`/logs/${f.id}`, body); else await api.post("/logs", body);
      toast.success(f.id ? "Log updated (previous version kept)" : "Workout logged");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit workout log" : "Log workout"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-2">
          <div className="col-span-2"><Label>Session</Label><Input value={f.day_name} onChange={(e) => setF({ ...f, day_name: e.target.value })} className="mt-1" data-testid="log-day-name-input" /></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1" data-testid="log-date-input" /></div>
          <div><Label>RPE (1-10)</Label><Input type="number" value={f.rpe || ""} onChange={(e) => setF({ ...f, rpe: Number(e.target.value) })} className="mt-1" data-testid="log-rpe-input" /></div>
        </div>
        <div className="space-y-3 mt-2">
          {f.entries.map((e, ei) => (
            <div key={ei} className="panel p-3" data-testid={`log-entry-${ei}`}>
              <div className="flex gap-2 mb-2">
                <Input value={e.exercise} onChange={(ev) => upd(ei, { exercise: ev.target.value })} placeholder="Exercise" className="h-9" data-testid={`log-exercise-${ei}`} />
                <Button variant="ghost" size="icon" onClick={() => setF({ ...f, entries: f.entries.filter((_, j) => j !== ei) })}><Trash2 size={14} /></Button>
              </div>
              {(e.target_rest || e.target_weight || e.target_time || e.target_distance) && <div className="text-xs text-slate-500 mb-2">Target: {[e.target_rest && `Rest ${e.target_rest}`, e.target_weight, e.target_time, e.target_distance].filter(Boolean).join(" · ")}</div>}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div><Label className="text-xs">Time completed</Label><Input value={e.time || ""} onChange={(ev) => upd(ei, { time: ev.target.value })} placeholder="e.g. 30 sec" className="mt-1 h-8" data-testid={`log-time-${ei}`} /></div>
                <div><Label className="text-xs">Distance completed</Label><Input value={e.distance || ""} onChange={(ev) => upd(ei, { distance: ev.target.value })} placeholder="e.g. 2 km" className="mt-1 h-8" data-testid={`log-distance-${ei}`} /></div>
              </div>
              {e.sets.map((s, si) => (
                <div key={si} className="flex items-center gap-3 mb-1 text-xs">
                  <span className="eyebrow w-10">Set {si + 1}</span>
                  <span className="text-slate-500">{s.weight_unit || e.weight_unit || "kg"}</span><Stepper value={s.weight} step={s.weight_unit === "lb" ? 5 : 2.5} onChange={(v) => updSet(ei, si, "weight", v)} testid={`log-weight-${ei}-${si}`} />
                  <span className="text-slate-500">reps</span><Stepper value={s.reps} step={1} onChange={(v) => updSet(ei, si, "reps", v)} testid={`log-reps-${ei}-${si}`} />
                </div>
              ))}
              <Button variant="ghost" size="sm" className="text-[#FF3B30]" onClick={() => upd(ei, { sets: [...e.sets, { ...(e.sets[e.sets.length - 1] || { reps: 10, weight: 0 }) }] })} data-testid={`log-add-set-${ei}`}><Plus size={12} className="mr-1" />Set</Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setF({ ...f, entries: [...f.entries, { exercise: "", notes: "", sets: [{ reps: 10, weight: 0 }] }] })} data-testid="log-add-exercise-btn"><Plus size={14} className="mr-1" />Exercise</Button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div><Label>Minutes</Label><Input type="number" value={f.duration_min || ""} onChange={(e) => setF({ ...f, duration_min: Number(e.target.value) })} className="mt-1" /></div>
          <div className="col-span-3"><Label>Notes</Label><Textarea rows={1} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" data-testid="log-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="log-workout-submit-btn">{f.id ? "Save changes" : "Submit log"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Logs() {
  const loc = useLocation();
  const nav = useNavigate();
  const [logs, reload] = useLoad("/logs");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (loc.state?.day) {
      setEditing({ date: today(), plan_id: loc.state.plan_id, day_name: loc.state.day.name, entries: fromDay(loc.state.day), notes: "" });
      nav(loc.pathname, { replace: true, state: null });
    }
  }, [loc.state, loc.pathname, nav]);

  const del = async (l) => { await api.delete(`/logs/${l.id}`); reload(); };
  return (
    <div>
      <PageHeader eyebrow="Training history" title="Workout logs" testid="logs-title">
        <Button onClick={() => setEditing({ date: today(), day_name: "", entries: [{ exercise: "", notes: "", sets: [{ reps: 10, weight: 0 }] }], notes: "" })} data-testid="new-log-btn">
          <Plus size={15} className="mr-1" />New log
        </Button>
      </PageHeader>
      {!logs ? <div className="eyebrow">Loading…</div> : !logs.length ? <Empty text="No workouts logged yet. Open My Plans and tap Log on a training day." /> : (
        <div className="space-y-3">
          {logs.map((l) => (
            <Section key={l.id} title={`${fmtDate(l.date)} · ${l.day_name || "Workout"}`} testid={`log-card-${l.id}`}
              action={<div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditing(l)} data-testid={`log-edit-btn-${l.id}`}><Pencil size={13} className="mr-1" />Edit</Button>
                <Button variant="ghost" size="icon" onClick={() => del(l)} data-testid={`log-delete-btn-${l.id}`}><Trash2 size={13} /></Button>
              </div>}>
              <div className="grid sm:grid-cols-2 gap-2">
                {l.entries.map((e, i) => (
                  <div key={i} className="text-sm"><span>{e.exercise}</span>
                    <span className="font-mono-k text-xs text-slate-400 ml-2">{e.sets.map((s) => `${s.weight ?? 0}${s.weight_unit || "kg"}×${s.reps ?? 0}`).join(", ")}</span>
                    {(e.time || e.distance) && <div className="text-xs text-slate-500">{[e.time, e.distance].filter(Boolean).join(" · ")}</div>}</div>
                ))}
              </div>
              {l.edited && <div className="text-xs text-slate-500 mt-2">Edited · {l.history.length} previous version(s) kept for your coach</div>}
            </Section>
          ))}
        </div>
      )}
      {editing && <LogDialog initial={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
}
